// UI string translations keyed by ISO 639-1 language code.
// First-pass translations; native speakers should refine.
//
// Country names are NOT translated here — use `countryNameIn(code, lang)` which
// delegates to the browser's built-in Intl.DisplayNames API (covers all 195
// countries in any locale automatically).

export type TKey =
  | 'hey'           // big homepage greeting, e.g. "Hey America!"
  | 'fromTo'        // card header template: "From {country} to {audience}:"
  | 'america'       // the word "America" (audience side)
  | 'world'         // the word/phrase "the World" (audience side)
  | 'writeYourOwn'  // CTA button under the card stack
  | 'all'           // filter button
  | 'yourCountry'   // country selector header
  | 'language'      // language selector header
  | 'search'        // country search placeholder
  // End-of-stack / empty states
  | 'seenAll'       // "You've seen them all!"
  | 'votedSummary'  // "You voted on {n} statements. Want to add your voice?"
  | 'voteCounter'   // small "{n} voted" pill
  | 'emptyLang'     // "No statements in this language yet"
  | 'emptyLangHint' // "Be the first to write one — or pick another language."
  // Verify modal
  | 'verifyTitle'   // "You voted on {n} statements!"
  | 'verifySubtitle' // "Verify you're human so your votes count."
  | 'sendMagicLink' // "Send magic link"
  | 'skipForNow'    // "Skip for now"
  | 'checkEmail'    // "Check your email!"
  | 'sentLinkTo'    // "We sent a verification link to"
  | 'continueSwiping' // "Continue swiping"
  // Write-your-own sheet
  | 'writePlaceholder' // "What do you want to say to {audience}?"
  | 'startOver'     // "Start over"
  | 'submit'        // "Submit"
  // Share modal
  | 'shareTitle'    // "Share this statement"
  | 'copyLink'      // "Copy link"
  | 'copied'        // "Copied!" — transient state after a successful copy
  | 'linkedinHint'  // "Text copied — paste it into your LinkedIn post"
  // Hero tagline (under "Hey America!")
  | 'tagline'       // "Every country can write to America. Vote on what hits hardest. Add your voice."
  // Help modal
  | 'helpTitle'     // "How it works"
  | 'helpIntro'     // "A place where every country can speak to America — and Americans speak back."
  | 'helpTap'       // "Tap a card to read it full-size and vote"
  | 'helpVote'      // "Thumbs up to agree, down to disagree, star for your strongest support"
  | 'helpWrite'     // "Write your own — your voice from your country"
  | 'helpLanguage'; // "Switch language any time — UI translates, cards filter"

type Translations = Partial<Record<TKey, string>>;

const T: Record<string, Translations> = {
  en: {
    hey: 'Hey America!',
    fromTo: 'From {country} to {audience}:',
    america: 'America',
    world: 'the World',
    writeYourOwn: 'Write your own',
    all: 'All',
    yourCountry: 'Your country',
    language: 'Language',
    search: 'Search countries...',
    seenAll: 'You\'ve seen them all!',
    votedSummary: 'You voted on {n} statements. Want to add your voice?',
    voteCounter: '{n} voted',
    emptyLang: 'No statements in this language yet',
    emptyLangHint: 'Be the first to write one — or pick another language.',
    verifyTitle: 'You voted on {n} statements!',
    verifySubtitle: 'Verify you\'re human so your votes count.',
    sendMagicLink: 'Send magic link',
    skipForNow: 'Skip for now',
    checkEmail: 'Check your email!',
    sentLinkTo: 'We sent a verification link to',
    continueSwiping: 'Continue swiping',
    writePlaceholder: 'What do you want to say to {audience}?',
    startOver: 'Start over',
    submit: 'Submit',
    shareTitle: 'Share this statement',
    copyLink: 'Copy link',
    copied: 'Copied!',
    linkedinHint: 'Text copied — paste it into your LinkedIn post',
    tagline: 'Every country can write to America. Vote on what hits hardest. Add your voice.',
    helpTitle: 'How it works',
    helpIntro: 'A place where every country can speak to America — and Americans speak back.',
    helpTap: 'Tap a card to read it and vote',
    helpVote: 'Thumbs up to agree, down to disagree, star for your strongest support',
    helpWrite: 'Write your own — your voice from your country',
    helpLanguage: 'Switch language any time',
  },
  es: {
    hey: '¡Oye, América!',
    fromTo: 'De {country} para {audience}:',
    america: 'América',
    world: 'el Mundo',
    writeYourOwn: 'Escribe el tuyo',
    all: 'Todos',
    yourCountry: 'Tu país',
    language: 'Idioma',
    search: 'Buscar países...',
    seenAll: '¡Los viste todos!',
    votedSummary: 'Votaste en {n} mensajes. ¿Quieres añadir tu voz?',
    voteCounter: '{n} votos',
    emptyLang: 'Aún no hay mensajes en este idioma',
    emptyLangHint: 'Sé el primero en escribir uno — o elige otro idioma.',
    verifyTitle: '¡Votaste en {n} mensajes!',
    verifySubtitle: 'Verifica que eres humano para que tus votos cuenten.',
    sendMagicLink: 'Enviar enlace mágico',
    skipForNow: 'Saltar por ahora',
    checkEmail: '¡Revisa tu correo!',
    sentLinkTo: 'Enviamos un enlace de verificación a',
    continueSwiping: 'Seguir',
    writePlaceholder: '¿Qué quieres decirle a {audience}?',
    startOver: 'Borrar',
    submit: 'Enviar',
    shareTitle: 'Comparte este mensaje',
    copyLink: 'Copiar enlace',
    copied: '¡Copiado!',
    linkedinHint: 'Texto copiado — pégalo en tu publicación de LinkedIn',
    tagline: 'Cada país puede escribirle a América. Vota por lo que más impacta. Añade tu voz.',
    helpTitle: 'Cómo funciona',
    helpIntro: 'Un lugar donde cada país puede hablarle a América — y los americanos responder.',
    helpTap: 'Toca un mensaje para leerlo y votar',
    helpVote: 'Pulgar arriba para aprobar, abajo para rechazar, estrella para tu apoyo más fuerte',
    helpWrite: 'Escribe el tuyo — tu voz desde tu país',
    helpLanguage: 'Cambia de idioma cuando quieras',
  },
  fr: {
    hey: 'Hé l’Amérique !',
    fromTo: 'De {country} à {audience} :',
    america: 'l’Amérique',
    world: 'le Monde',
    writeYourOwn: 'Écris le tien',
    all: 'Tout',
    yourCountry: 'Ton pays',
    language: 'Langue',
    search: 'Rechercher des pays...',
    seenAll: 'Tu les as tous vus !',
    votedSummary: 'Tu as voté sur {n} messages. Tu veux ajouter ta voix ?',
    voteCounter: '{n} votes',
    emptyLang: 'Aucun message dans cette langue',
    emptyLangHint: 'Sois le premier à en écrire un — ou choisis une autre langue.',
    verifyTitle: 'Tu as voté sur {n} messages !',
    verifySubtitle: 'Vérifie que tu es humain pour que tes votes comptent.',
    sendMagicLink: 'Envoyer le lien magique',
    skipForNow: 'Plus tard',
    checkEmail: 'Vérifie tes e-mails !',
    sentLinkTo: 'Nous avons envoyé un lien de vérification à',
    continueSwiping: 'Continuer',
    writePlaceholder: 'Que veux-tu dire à {audience} ?',
    startOver: 'Effacer',
    submit: 'Envoyer',
    shareTitle: 'Partager ce message',
    copyLink: 'Copier le lien',
    copied: 'Copié !',
    linkedinHint: 'Texte copié — colle-le dans ton post LinkedIn',
    tagline: 'Chaque pays peut écrire à l\'Amérique. Vote pour ce qui frappe le plus. Ajoute ta voix.',
    helpTitle: 'Comment ça marche',
    helpIntro: 'Un lieu où chaque pays peut parler à l\'Amérique — et les Américains répondre.',
    helpTap: 'Touche une carte pour la lire et voter',
    helpVote: 'Pouce levé pour approuver, baissé pour rejeter, étoile pour ton soutien le plus fort',
    helpWrite: 'Écris le tien — ta voix depuis ton pays',
    helpLanguage: 'Change de langue quand tu veux',
  },
  pt: {
    hey: 'Ei, América!',
    fromTo: 'De {country} para {audience}:',
    america: 'América',
    world: 'o Mundo',
    writeYourOwn: 'Escreva o seu',
    all: 'Todos',
    yourCountry: 'Seu país',
    language: 'Idioma',
    search: 'Buscar países...',
    seenAll: 'Você viu tudo!',
    votedSummary: 'Você votou em {n} mensagens. Quer adicionar sua voz?',
    voteCounter: '{n} votos',
    emptyLang: 'Ainda não há mensagens neste idioma',
    emptyLangHint: 'Seja o primeiro a escrever — ou escolha outro idioma.',
    verifyTitle: 'Você votou em {n} mensagens!',
    verifySubtitle: 'Verifique que é humano para seus votos contarem.',
    sendMagicLink: 'Enviar link mágico',
    skipForNow: 'Agora não',
    checkEmail: 'Verifique seu e-mail!',
    sentLinkTo: 'Enviamos um link de verificação para',
    continueSwiping: 'Continuar',
    writePlaceholder: 'O que você quer dizer a {audience}?',
    startOver: 'Limpar',
    submit: 'Enviar',
    shareTitle: 'Compartilhar esta mensagem',
    copyLink: 'Copiar link',
    copied: 'Copiado!',
    linkedinHint: 'Texto copiado — cole no seu post do LinkedIn',
    tagline: 'Cada país pode escrever para a América. Vote no que mais marca. Adicione sua voz.',
  },
  de: {
    hey: 'Hey Amerika!',
    fromTo: 'Von {country} an {audience}:',
    america: 'Amerika',
    world: 'die Welt',
    writeYourOwn: 'Schreib deine eigene',
    all: 'Alle',
    yourCountry: 'Dein Land',
    language: 'Sprache',
    search: 'Länder suchen...',
    seenAll: 'Du hast alle gesehen!',
    votedSummary: 'Du hast über {n} Nachrichten abgestimmt. Möchtest du deine Stimme hinzufügen?',
    voteCounter: '{n} Stimmen',
    emptyLang: 'Noch keine Nachrichten in dieser Sprache',
    emptyLangHint: 'Sei der Erste — oder wähle eine andere Sprache.',
    verifyTitle: 'Du hast über {n} Nachrichten abgestimmt!',
    verifySubtitle: 'Bestätige, dass du ein Mensch bist, damit deine Stimmen zählen.',
    sendMagicLink: 'Magic-Link senden',
    skipForNow: 'Später',
    checkEmail: 'Schau in dein Postfach!',
    sentLinkTo: 'Wir haben einen Bestätigungslink gesendet an',
    continueSwiping: 'Weitermachen',
    writePlaceholder: 'Was willst du {audience} sagen?',
    startOver: 'Zurücksetzen',
    submit: 'Senden',
    shareTitle: 'Diese Nachricht teilen',
    copyLink: 'Link kopieren',
    copied: 'Kopiert!',
    linkedinHint: 'Text kopiert — füge ihn in deinen LinkedIn-Beitrag ein',
    tagline: 'Jedes Land kann an Amerika schreiben. Stimme über das ab, was am meisten trifft. Füge deine Stimme hinzu.',
    helpTitle: 'So funktioniert\'s',
    helpIntro: 'Ein Ort, an dem jedes Land zu Amerika sprechen kann — und Amerikaner antworten.',
    helpTap: 'Tippe eine Karte an, um sie zu lesen und abzustimmen',
    helpVote: 'Daumen hoch zum Zustimmen, runter zum Ablehnen, Stern für die stärkste Unterstützung',
    helpWrite: 'Schreib deine eigene — deine Stimme aus deinem Land',
    helpLanguage: 'Wechsle die Sprache jederzeit',
  },
  it: {
    hey: 'Ehi, America!',
    fromTo: 'Da {country} a {audience}:',
    america: 'America',
    world: 'il Mondo',
    writeYourOwn: 'Scrivi il tuo',
    all: 'Tutti',
    yourCountry: 'Il tuo paese',
    language: 'Lingua',
    search: 'Cerca paesi...',
    seenAll: 'Li hai visti tutti!',
    votedSummary: 'Hai votato su {n} messaggi. Vuoi aggiungere la tua voce?',
    voteCounter: '{n} voti',
    emptyLang: 'Nessun messaggio in questa lingua',
    emptyLangHint: 'Sii il primo a scriverne uno — o scegli un\'altra lingua.',
    verifyTitle: 'Hai votato su {n} messaggi!',
    verifySubtitle: 'Verifica di essere umano perché i tuoi voti contino.',
    sendMagicLink: 'Invia magic link',
    skipForNow: 'Più tardi',
    checkEmail: 'Controlla la tua email!',
    sentLinkTo: 'Abbiamo inviato un link di verifica a',
    continueSwiping: 'Continua',
    writePlaceholder: 'Cosa vuoi dire a {audience}?',
    startOver: 'Cancella',
    submit: 'Invia',
    shareTitle: 'Condividi questo messaggio',
    copyLink: 'Copia link',
  },
  nl: {
    hey: 'Hé Amerika!',
    fromTo: 'Van {country} aan {audience}:',
    america: 'Amerika',
    world: 'de Wereld',
    writeYourOwn: 'Schrijf je eigen',
    all: 'Alle',
    yourCountry: 'Jouw land',
    language: 'Taal',
    search: 'Landen zoeken...',
    seenAll: 'Je hebt ze allemaal gezien!',
    votedSummary: 'Je stemde op {n} berichten. Wil je je stem toevoegen?',
    voteCounter: '{n} stemmen',
    emptyLang: 'Nog geen berichten in deze taal',
    emptyLangHint: 'Schrijf de eerste — of kies een andere taal.',
    verifyTitle: 'Je stemde op {n} berichten!',
    verifySubtitle: 'Verifieer dat je een mens bent zodat je stemmen tellen.',
    sendMagicLink: 'Verstuur magic link',
    skipForNow: 'Later',
    checkEmail: 'Controleer je e-mail!',
    sentLinkTo: 'We hebben een verificatielink gestuurd naar',
    continueSwiping: 'Doorgaan',
    writePlaceholder: 'Wat wil je tegen {audience} zeggen?',
    startOver: 'Wissen',
    submit: 'Versturen',
    shareTitle: 'Deel dit bericht',
    copyLink: 'Link kopiëren',
  },
  ru: {
    hey: 'Эй, Америка!',
    fromTo: 'Из {country} в {audience}:',
    america: 'Америку',
    world: 'мир',
    writeYourOwn: 'Напиши своё',
    all: 'Все',
    yourCountry: 'Твоя страна',
    language: 'Язык',
    search: 'Поиск стран...',
    seenAll: 'Ты увидел все!',
    votedSummary: 'Ты проголосовал за {n} сообщений. Хочешь добавить свой голос?',
    voteCounter: '{n} голосов',
    emptyLang: 'На этом языке пока нет сообщений',
    emptyLangHint: 'Будь первым — или выбери другой язык.',
    verifyTitle: 'Ты проголосовал за {n} сообщений!',
    verifySubtitle: 'Подтверди, что ты человек, чтобы твои голоса учитывались.',
    sendMagicLink: 'Отправить ссылку',
    skipForNow: 'Позже',
    checkEmail: 'Проверь почту!',
    sentLinkTo: 'Мы отправили ссылку для подтверждения на',
    continueSwiping: 'Продолжить',
    writePlaceholder: 'Что ты хочешь сказать {audience}?',
    startOver: 'Очистить',
    submit: 'Отправить',
    shareTitle: 'Поделиться этим сообщением',
    copyLink: 'Скопировать ссылку',
    copied: 'Скопировано!',
    linkedinHint: 'Текст скопирован — вставьте его в свой пост LinkedIn',
    tagline: 'Каждая страна может написать Америке. Голосуй за то, что попадает в самую точку. Добавь свой голос.',
  },
  uk: {
    hey: 'Гей, Америко!',
    fromTo: 'З {country} до {audience}:',
    america: 'Америки',
    world: 'світу',
    writeYourOwn: 'Напиши своє',
    all: 'Усі',
    yourCountry: 'Твоя країна',
    language: 'Мова',
    search: 'Пошук країн...',
    seenAll: 'Ти побачив усі!',
    votedSummary: 'Ти проголосував за {n} повідомлень. Хочеш додати свій голос?',
    voteCounter: '{n} голосів',
    emptyLang: 'Цією мовою ще немає повідомлень',
    emptyLangHint: 'Будь першим — або обери іншу мову.',
    verifyTitle: 'Ти проголосував за {n} повідомлень!',
    verifySubtitle: 'Підтверди, що ти людина, щоб твої голоси враховувалися.',
    sendMagicLink: 'Надіслати посилання',
    skipForNow: 'Пізніше',
    checkEmail: 'Перевір пошту!',
    sentLinkTo: 'Ми надіслали посилання для підтвердження на',
    continueSwiping: 'Продовжити',
    writePlaceholder: 'Що ти хочеш сказати {audience}?',
    startOver: 'Очистити',
    submit: 'Надіслати',
    shareTitle: 'Поділитися повідомленням',
    copyLink: 'Скопіювати посилання',
  },
  pl: {
    hey: 'Hej, Ameryko!',
    fromTo: 'Z {country} do {audience}:',
    america: 'Ameryki',
    world: 'Świata',
    writeYourOwn: 'Napisz własne',
    all: 'Wszystkie',
    yourCountry: 'Twój kraj',
    language: 'Język',
    search: 'Szukaj krajów...',
    seenAll: 'Zobaczyłeś wszystkie!',
    votedSummary: 'Zagłosowałeś na {n} wiadomości. Chcesz dodać swój głos?',
    voteCounter: '{n} głosów',
    emptyLang: 'Brak wiadomości w tym języku',
    emptyLangHint: 'Bądź pierwszy — lub wybierz inny język.',
    verifyTitle: 'Zagłosowałeś na {n} wiadomości!',
    verifySubtitle: 'Potwierdź, że jesteś człowiekiem, aby twoje głosy się liczyły.',
    sendMagicLink: 'Wyślij magic link',
    skipForNow: 'Później',
    checkEmail: 'Sprawdź pocztę!',
    sentLinkTo: 'Wysłaliśmy link weryfikacyjny do',
    continueSwiping: 'Kontynuuj',
    writePlaceholder: 'Co chcesz powiedzieć {audience}?',
    startOver: 'Wyczyść',
    submit: 'Wyślij',
    shareTitle: 'Udostępnij wiadomość',
    copyLink: 'Skopiuj link',
  },
  cs: {
    hey: 'Hej, Ameriko!',
    fromTo: 'Z {country} do {audience}:',
    america: 'Ameriky',
    world: 'světa',
    writeYourOwn: 'Napiš vlastní',
    all: 'Vše',
    yourCountry: 'Tvoje země',
    language: 'Jazyk',
    search: 'Hledat země...',
  },
  sk: {
    hey: 'Hej, Amerika!',
    fromTo: 'Z {country} do {audience}:',
    america: 'Ameriky',
    world: 'sveta',
    writeYourOwn: 'Napíš vlastné',
    all: 'Všetko',
    yourCountry: 'Tvoja krajina',
    language: 'Jazyk',
    search: 'Hľadať krajiny...',
  },
  sv: {
    hey: 'Hej Amerika!',
    fromTo: 'Från {country} till {audience}:',
    america: 'Amerika',
    world: 'världen',
    writeYourOwn: 'Skriv ditt eget',
    all: 'Alla',
    yourCountry: 'Ditt land',
    language: 'Språk',
    search: 'Sök länder...',
    seenAll: 'Du har sett alla!',
    votedSummary: 'Du röstade på {n} meddelanden. Vill du lägga till din röst?',
    voteCounter: '{n} röster',
    emptyLang: 'Inga meddelanden på detta språk än',
    emptyLangHint: 'Var först — eller välj ett annat språk.',
    verifyTitle: 'Du röstade på {n} meddelanden!',
    verifySubtitle: 'Verifiera att du är människa så dina röster räknas.',
    sendMagicLink: 'Skicka magisk länk',
    skipForNow: 'Senare',
    checkEmail: 'Kolla din e-post!',
    sentLinkTo: 'Vi skickade en verifieringslänk till',
    continueSwiping: 'Fortsätt',
    writePlaceholder: 'Vad vill du säga till {audience}?',
    startOver: 'Rensa',
    submit: 'Skicka',
    shareTitle: 'Dela detta meddelande',
    copyLink: 'Kopiera länk',
  },
  no: {
    hey: 'Hei Amerika!',
    fromTo: 'Fra {country} til {audience}:',
    america: 'Amerika',
    world: 'verden',
    writeYourOwn: 'Skriv din egen',
    all: 'Alle',
    yourCountry: 'Ditt land',
    language: 'Språk',
    search: 'Søk land...',
  },
  da: {
    hey: 'Hej Amerika!',
    fromTo: 'Fra {country} til {audience}:',
    america: 'Amerika',
    world: 'verden',
    writeYourOwn: 'Skriv din egen',
    all: 'Alle',
    yourCountry: 'Dit land',
    language: 'Sprog',
    search: 'Søg lande...',
  },
  fi: {
    hey: 'Hei Amerikka!',
    fromTo: '{country}sta {audience}:',
    america: 'Amerikalle',
    world: 'maailmalle',
    writeYourOwn: 'Kirjoita omasi',
    all: 'Kaikki',
    yourCountry: 'Maasi',
    language: 'Kieli',
    search: 'Etsi maita...',
  },
  is: {
    hey: 'Hæ Ameríka!',
    fromTo: 'Frá {country} til {audience}:',
    america: 'Ameríku',
    world: 'heimsins',
    writeYourOwn: 'Skrifaðu þitt eigið',
    all: 'Allt',
    yourCountry: 'Land þitt',
    language: 'Tungumál',
    search: 'Leita að löndum...',
  },
  el: {
    hey: 'Έι, Αμερική!',
    fromTo: 'Από {country} προς {audience}:',
    america: 'την Αμερική',
    world: 'τον Κόσμο',
    writeYourOwn: 'Γράψε το δικό σου',
    all: 'Όλα',
    yourCountry: 'Η χώρα σου',
    language: 'Γλώσσα',
    search: 'Αναζήτηση χωρών...',
  },
  hu: {
    hey: 'Hé, Amerika!',
    fromTo: '{country} → {audience}:',
    america: 'Amerikának',
    world: 'a Világnak',
    writeYourOwn: 'Írd meg a sajátodat',
    all: 'Mind',
    yourCountry: 'Az országod',
    language: 'Nyelv',
    search: 'Országok keresése...',
  },
  ro: {
    hey: 'Hei, America!',
    fromTo: 'Din {country} către {audience}:',
    america: 'America',
    world: 'Lume',
    writeYourOwn: 'Scrie-l pe al tău',
    all: 'Toate',
    yourCountry: 'Țara ta',
    language: 'Limbă',
    search: 'Caută țări...',
  },
  bg: {
    hey: 'Хей, Америка!',
    fromTo: 'От {country} до {audience}:',
    america: 'Америка',
    world: 'Света',
    writeYourOwn: 'Напиши свой',
    all: 'Всички',
    yourCountry: 'Твоята страна',
    language: 'Език',
    search: 'Търсене на държави...',
  },
  sr: {
    hey: 'Хеј, Америко!',
    fromTo: 'Из {country} за {audience}:',
    america: 'Америку',
    world: 'свет',
    writeYourOwn: 'Напиши своје',
    all: 'Сви',
    yourCountry: 'Твоја држава',
    language: 'Језик',
    search: 'Тражи државе...',
  },
  hr: {
    hey: 'Hej, Ameriko!',
    fromTo: 'Iz {country} za {audience}:',
    america: 'Ameriku',
    world: 'svijet',
    writeYourOwn: 'Napiši svoje',
    all: 'Sve',
    yourCountry: 'Tvoja zemlja',
    language: 'Jezik',
    search: 'Traži zemlje...',
  },
  bs: {
    hey: 'Hej Amerika!',
    fromTo: 'Iz {country} za {audience}:',
    america: 'Ameriku',
    world: 'svijet',
    writeYourOwn: 'Napiši svoje',
    all: 'Sve',
    yourCountry: 'Tvoja zemlja',
    language: 'Jezik',
    search: 'Traži zemlje...',
  },
  sl: {
    hey: 'Hej, Amerika!',
    fromTo: 'Iz {country} v {audience}:',
    america: 'Ameriko',
    world: 'svet',
    writeYourOwn: 'Napiši svojo',
    all: 'Vse',
    yourCountry: 'Tvoja država',
    language: 'Jezik',
    search: 'Iskanje držav...',
  },
  mk: {
    hey: 'Еј, Америко!',
    fromTo: 'Од {country} до {audience}:',
    america: 'Америка',
    world: 'Светот',
    writeYourOwn: 'Напиши свое',
    all: 'Сите',
    yourCountry: 'Твојата земја',
    language: 'Јазик',
    search: 'Барај земји...',
  },
  be: {
    hey: 'Гэй, Амерыка!',
    fromTo: 'З {country} да {audience}:',
    america: 'Амерыкі',
    world: 'свету',
    writeYourOwn: 'Напішы сваё',
    all: 'Усе',
    yourCountry: 'Твая краіна',
    language: 'Мова',
    search: 'Пошук краін...',
  },
  lt: {
    hey: 'Ei, Amerika!',
    fromTo: 'Iš {country} į {audience}:',
    america: 'Ameriką',
    world: 'Pasaulį',
    writeYourOwn: 'Parašyk savo',
    all: 'Visi',
    yourCountry: 'Tavo šalis',
    language: 'Kalba',
    search: 'Ieškoti šalių...',
  },
  lv: {
    hey: 'Hei, Amerika!',
    fromTo: 'No {country} uz {audience}:',
    america: 'Ameriku',
    world: 'pasauli',
    writeYourOwn: 'Uzraksti savu',
    all: 'Visi',
    yourCountry: 'Tava valsts',
    language: 'Valoda',
    search: 'Meklēt valstis...',
  },
  et: {
    hey: 'Hei Ameerika!',
    fromTo: '{country}st {audience}:',
    america: 'Ameerikale',
    world: 'maailmale',
    writeYourOwn: 'Kirjuta enda oma',
    all: 'Kõik',
    yourCountry: 'Sinu riik',
    language: 'Keel',
    search: 'Otsi riike...',
  },
  tr: {
    hey: 'Hey Amerika!',
    fromTo: '{country}\'dan {audience}:',
    america: 'Amerika\'ya',
    world: 'Dünya\'ya',
    writeYourOwn: 'Kendi mesajını yaz',
    all: 'Tümü',
    yourCountry: 'Ülken',
    language: 'Dil',
    search: 'Ülke ara...',
    seenAll: 'Hepsini gördün!',
    votedSummary: '{n} mesaja oy verdin. Sesini eklemek ister misin?',
    voteCounter: '{n} oy',
    emptyLang: 'Bu dilde henüz mesaj yok',
    emptyLangHint: 'İlk yazan sen ol — ya da başka bir dil seç.',
    verifyTitle: '{n} mesaja oy verdin!',
    verifySubtitle: 'Oylarının sayılması için insan olduğunu doğrula.',
    sendMagicLink: 'Sihirli bağlantı gönder',
    skipForNow: 'Şimdilik geç',
    checkEmail: 'E-postanı kontrol et!',
    sentLinkTo: 'Doğrulama bağlantısını şuraya gönderdik:',
    continueSwiping: 'Devam et',
    writePlaceholder: '{audience} ne demek istiyorsun?',
    startOver: 'Temizle',
    submit: 'Gönder',
    shareTitle: 'Bu mesajı paylaş',
    copyLink: 'Bağlantıyı kopyala',
  },
  az: {
    hey: 'Hey Amerika!',
    fromTo: '{country}-dan {audience}:',
    america: 'Amerikaya',
    world: 'Dünyaya',
    writeYourOwn: 'Öz mesajını yaz',
    all: 'Hamısı',
    yourCountry: 'Ölkən',
    language: 'Dil',
    search: 'Ölkə axtar...',
  },
  ar: {
    hey: 'يا أمريكا!',
    fromTo: 'من {country} إلى {audience}:',
    america: 'أمريكا',
    world: 'العالم',
    writeYourOwn: 'اكتب رسالتك',
    all: 'الكل',
    yourCountry: 'بلدك',
    language: 'اللغة',
    search: 'ابحث عن دولة...',
    seenAll: 'لقد رأيتها كلها!',
    votedSummary: 'لقد صوّت على {n} رسالة. هل تريد إضافة صوتك؟',
    voteCounter: '{n} صوت',
    emptyLang: 'لا توجد رسائل بهذه اللغة بعد',
    emptyLangHint: 'كن أول من يكتب — أو اختر لغة أخرى.',
    verifyTitle: 'لقد صوّت على {n} رسالة!',
    verifySubtitle: 'تحقق أنك إنسان حتى تُحتسب أصواتك.',
    sendMagicLink: 'أرسل الرابط',
    skipForNow: 'لاحقًا',
    checkEmail: 'تحقق من بريدك!',
    sentLinkTo: 'أرسلنا رابط التحقق إلى',
    continueSwiping: 'استمر',
    writePlaceholder: 'ماذا تريد أن تقول لـ {audience}؟',
    startOver: 'مسح',
    submit: 'أرسل',
    shareTitle: 'شارك هذه الرسالة',
    copyLink: 'انسخ الرابط',
    copied: 'تم النسخ!',
    linkedinHint: 'تم نسخ النص — الصقه في منشور لينكدإن',
    tagline: 'كل بلد يستطيع أن يخاطب أمريكا. صوّت لما يلامس أعمق نقطة. أضف صوتك.',
    helpTitle: 'كيف يعمل',
    helpIntro: 'مكان حيث يمكن لكل بلد أن يخاطب أمريكا — وحيث يرد الأمريكيون.',
    helpTap: 'اضغط على بطاقة لقراءتها والتصويت',
    helpVote: 'إبهام للأعلى للموافقة، للأسفل للرفض، نجمة لأقوى دعم',
    helpWrite: 'اكتب رسالتك — صوتك من بلدك',
    helpLanguage: 'بدّل اللغة في أي وقت',
  },
  he: {
    hey: 'היי אמריקה!',
    fromTo: 'מ{country} ל{audience}:',
    america: 'אמריקה',
    world: 'עולם',
    writeYourOwn: 'כתוב משלך',
    all: 'הכול',
    yourCountry: 'הארץ שלך',
    language: 'שפה',
    search: 'חיפוש מדינות...',
  },
  fa: {
    hey: 'هی آمریکا!',
    fromTo: 'از {country} به {audience}:',
    america: 'آمریکا',
    world: 'جهان',
    writeYourOwn: 'پیام خودت را بنویس',
    all: 'همه',
    yourCountry: 'کشور تو',
    language: 'زبان',
    search: 'جستجوی کشورها...',
  },
  ur: {
    hey: 'ارے امریکہ!',
    fromTo: '{country} سے {audience} تک:',
    america: 'امریکہ',
    world: 'دنیا',
    writeYourOwn: 'اپنا پیغام لکھیں',
    all: 'سب',
    yourCountry: 'آپ کا ملک',
    language: 'زبان',
    search: 'ممالک تلاش کریں...',
  },
  ps: {
    hey: 'هی امریکا!',
    fromTo: 'له {country} څخه {audience} ته:',
    america: 'امریکا',
    world: 'نړۍ',
    writeYourOwn: 'خپل پیغام ولیکه',
    all: 'ټول',
    yourCountry: 'ستاسو هیواد',
    language: 'ژبه',
    search: 'هیوادونه ولټوه...',
  },
  ku: {
    hey: 'Hey Emerîka!',
    fromTo: 'Ji {country} ji {audience} re:',
    america: 'Emerîkayê',
    world: 'Cîhanê',
    writeYourOwn: 'Yê xwe binivîse',
    all: 'Hemû',
    yourCountry: 'Welatê te',
    language: 'Ziman',
    search: 'Welatan bigere...',
  },
  hi: {
    hey: 'सुनो अमेरिका!',
    fromTo: '{country} से {audience} के लिए:',
    america: 'अमेरिका',
    world: 'दुनिया',
    writeYourOwn: 'अपना लिखो',
    all: 'सब',
    yourCountry: 'आपका देश',
    language: 'भाषा',
    search: 'देश खोजें...',
    seenAll: 'आपने सब देख लिए!',
    votedSummary: 'आपने {n} संदेशों पर वोट किया। अपनी आवाज़ जोड़ना चाहते हैं?',
    voteCounter: '{n} वोट',
    emptyLang: 'इस भाषा में अभी कोई संदेश नहीं',
    emptyLangHint: 'पहला लिखने वाले बनो — या दूसरी भाषा चुनो।',
    verifyTitle: 'आपने {n} संदेशों पर वोट किया!',
    verifySubtitle: 'पुष्टि करें कि आप मानव हैं ताकि आपके वोट गिने जाएं।',
    sendMagicLink: 'मैजिक लिंक भेजें',
    skipForNow: 'अभी छोड़ें',
    checkEmail: 'अपना ईमेल देखें!',
    sentLinkTo: 'हमने सत्यापन लिंक भेजा है',
    continueSwiping: 'जारी रखें',
    writePlaceholder: 'आप {audience} से क्या कहना चाहते हैं?',
    startOver: 'मिटाएं',
    submit: 'भेजें',
    shareTitle: 'यह संदेश साझा करें',
    copyLink: 'लिंक कॉपी करें',
    copied: 'कॉपी हो गया!',
    linkedinHint: 'टेक्स्ट कॉपी हो गया — LinkedIn पोस्ट में पेस्ट करें',
    tagline: 'हर देश अमेरिका को लिख सकता है। जो सबसे ज़ोर से लगे उसे वोट दो। अपनी आवाज़ जोड़ो।',
  },
  bn: {
    hey: 'এই যে, আমেরিকা!',
    fromTo: '{country} থেকে {audience} কে:',
    america: 'আমেরিকা',
    world: 'বিশ্ব',
    writeYourOwn: 'নিজের লিখুন',
    all: 'সব',
    yourCountry: 'আপনার দেশ',
    language: 'ভাষা',
    search: 'দেশ খুঁজুন...',
    seenAll: 'আপনি সব দেখে ফেলেছেন!',
    votedSummary: 'আপনি {n}টি বার্তায় ভোট দিয়েছেন। আপনার কণ্ঠ যোগ করতে চান?',
    voteCounter: '{n} ভোট',
    emptyLang: 'এই ভাষায় এখনো কোনো বার্তা নেই',
    emptyLangHint: 'প্রথম হোন — বা অন্য ভাষা বেছে নিন।',
    verifyTitle: 'আপনি {n}টি বার্তায় ভোট দিয়েছেন!',
    verifySubtitle: 'যাচাই করুন যে আপনি মানুষ যাতে আপনার ভোট গণনা হয়।',
    sendMagicLink: 'ম্যাজিক লিঙ্ক পাঠান',
    skipForNow: 'এখন এড়িয়ে যান',
    checkEmail: 'আপনার ইমেল দেখুন!',
    sentLinkTo: 'আমরা যাচাইকরণ লিঙ্ক পাঠিয়েছি',
    continueSwiping: 'চালিয়ে যান',
    writePlaceholder: '{audience}কে আপনি কী বলতে চান?',
    startOver: 'মুছে ফেলুন',
    submit: 'পাঠান',
    shareTitle: 'এই বার্তা শেয়ার করুন',
    copyLink: 'লিঙ্ক কপি করুন',
  },
  pa: {
    hey: 'ਹੇ ਅਮਰੀਕਾ!',
    fromTo: '{country} ਤੋਂ {audience} ਨੂੰ:',
    america: 'ਅਮਰੀਕਾ',
    world: 'ਦੁਨੀਆਂ',
    writeYourOwn: 'ਆਪਣਾ ਲਿਖੋ',
    all: 'ਸਾਰੇ',
    yourCountry: 'ਤੁਹਾਡਾ ਦੇਸ਼',
    language: 'ਭਾਸ਼ਾ',
    search: 'ਦੇਸ਼ ਖੋਜੋ...',
  },
  gu: {
    hey: 'એ અમેરિકા!',
    fromTo: '{country} થી {audience} માટે:',
    america: 'અમેરિકા',
    world: 'દુનિયા',
    writeYourOwn: 'તમારું લખો',
    all: 'બધા',
    yourCountry: 'તમારો દેશ',
    language: 'ભાષા',
    search: 'દેશો શોધો...',
  },
  mr: {
    hey: 'ए अमेरिका!',
    fromTo: '{country} पासून {audience} साठी:',
    america: 'अमेरिका',
    world: 'जगाला',
    writeYourOwn: 'स्वतःचा लिहा',
    all: 'सर्व',
    yourCountry: 'तुमचा देश',
    language: 'भाषा',
    search: 'देश शोधा...',
  },
  ne: {
    hey: 'ए अमेरिका!',
    fromTo: '{country} बाट {audience} लाई:',
    america: 'अमेरिका',
    world: 'विश्व',
    writeYourOwn: 'आफ्नै लेख्नुहोस्',
    all: 'सबै',
    yourCountry: 'तपाईंको देश',
    language: 'भाषा',
    search: 'देशहरू खोज्नुहोस्...',
  },
  ta: {
    hey: 'ஏய் அமெரிக்கா!',
    fromTo: '{country} இலிருந்து {audience}க்கு:',
    america: 'அமெரிக்கா',
    world: 'உலகம்',
    writeYourOwn: 'உங்களுடையது எழுதுங்கள்',
    all: 'அனைத்தும்',
    yourCountry: 'உங்கள் நாடு',
    language: 'மொழி',
    search: 'நாடுகளைத் தேடு...',
  },
  te: {
    hey: 'హే అమెరికా!',
    fromTo: '{country} నుండి {audience}కి:',
    america: 'అమెరికా',
    world: 'ప్రపంచం',
    writeYourOwn: 'మీది రాయండి',
    all: 'అన్నీ',
    yourCountry: 'మీ దేశం',
    language: 'భాష',
    search: 'దేశాలను శోధించండి...',
  },
  kn: {
    hey: 'ಹೇ ಅಮೆರಿಕ!',
    fromTo: '{country} ನಿಂದ {audience} ಗೆ:',
    america: 'ಅಮೆರಿಕ',
    world: 'ಪ್ರಪಂಚ',
    writeYourOwn: 'ನಿಮ್ಮದು ಬರೆಯಿರಿ',
    all: 'ಎಲ್ಲ',
    yourCountry: 'ನಿಮ್ಮ ದೇಶ',
    language: 'ಭಾಷೆ',
    search: 'ದೇಶಗಳನ್ನು ಹುಡುಕಿ...',
  },
  ml: {
    hey: 'ഹേയ് അമേരിക്ക!',
    fromTo: '{country} നിന്ന് {audience}ലേക്ക്:',
    america: 'അമേരിക്ക',
    world: 'ലോകം',
    writeYourOwn: 'നിങ്ങളുടേത് എഴുതുക',
    all: 'എല്ലാം',
    yourCountry: 'നിങ്ങളുടെ രാജ്യം',
    language: 'ഭാഷ',
    search: 'രാജ്യങ്ങൾ തിരയുക...',
  },
  si: {
    hey: 'හේයි ඇමරිකාව!',
    fromTo: '{country} සිට {audience}ට:',
    america: 'ඇමරිකාව',
    world: 'ලෝකය',
    writeYourOwn: 'ඔබේම ලියන්න',
    all: 'සියල්ල',
    yourCountry: 'ඔබේ රට',
    language: 'භාෂාව',
    search: 'රටවල් සොයන්න...',
  },
  or: {
    hey: 'ହେ ଆମେରିକା!',
    fromTo: '{country}ରୁ {audience}କୁ:',
    america: 'ଆମେରିକା',
    world: 'ବିଶ୍ୱ',
    writeYourOwn: 'ନିଜର ଲେଖନ୍ତୁ',
    all: 'ସବୁ',
    yourCountry: 'ଆପଣଙ୍କ ଦେଶ',
    language: 'ଭାଷା',
    search: 'ଦେଶ ଖୋଜନ୍ତୁ...',
  },
  zh: {
    hey: '嘿，美国！',
    fromTo: '从 {country} 给 {audience}：',
    america: '美国',
    world: '世界',
    writeYourOwn: '写下你的话',
    all: '全部',
    yourCountry: '你的国家',
    language: '语言',
    search: '搜索国家...',
    seenAll: '你都看完了！',
    votedSummary: '你为 {n} 条消息投了票。想加入你的声音吗？',
    voteCounter: '{n} 票',
    emptyLang: '这个语言还没有消息',
    emptyLangHint: '成为第一个写的人 — 或者选择另一种语言。',
    verifyTitle: '你为 {n} 条消息投了票！',
    verifySubtitle: '验证你是真人，让你的投票生效。',
    sendMagicLink: '发送魔法链接',
    skipForNow: '稍后再说',
    checkEmail: '查看你的邮箱！',
    sentLinkTo: '我们已发送验证链接到',
    continueSwiping: '继续',
    writePlaceholder: '你想对{audience}说什么？',
    startOver: '清空',
    submit: '发送',
    shareTitle: '分享这条消息',
    copyLink: '复制链接',
    copied: '已复制！',
    linkedinHint: '已复制文字 — 粘贴到你的领英帖子',
    tagline: '每个国家都能向美国发声。为最有力的话投票。加上你的声音。',
    helpTitle: '使用方法',
    helpIntro: '每个国家都能向美国发声 — 美国人也能回应的地方。',
    helpTap: '点击卡片阅读并投票',
    helpVote: '赞同、反对、收藏（最强支持）',
    helpWrite: '写下你的话 — 来自你国家的声音',
    helpLanguage: '随时切换语言',
  },
  ja: {
    hey: 'ねえ、アメリカ！',
    fromTo: '{country}から{audience}へ：',
    america: 'アメリカ',
    world: '世界',
    writeYourOwn: '自分の言葉で書く',
    all: 'すべて',
    yourCountry: 'あなたの国',
    language: '言語',
    search: '国を検索...',
    seenAll: '全部見ました！',
    votedSummary: '{n}件のメッセージに投票しました。あなたの声も加えませんか？',
    voteCounter: '{n}票',
    emptyLang: 'この言語のメッセージはまだありません',
    emptyLangHint: '最初の一人になろう — または別の言語を選んでください。',
    verifyTitle: '{n}件のメッセージに投票しました！',
    verifySubtitle: '票が反映されるよう、人間であることを確認してください。',
    sendMagicLink: 'マジックリンクを送る',
    skipForNow: '後で',
    checkEmail: 'メールを確認！',
    sentLinkTo: '確認リンクを送りました：',
    continueSwiping: '続ける',
    writePlaceholder: '{audience}に何を伝えたいですか？',
    startOver: 'クリア',
    submit: '送信',
    shareTitle: 'このメッセージを共有',
    copyLink: 'リンクをコピー',
    copied: 'コピーしました！',
    linkedinHint: 'テキストをコピーしました — LinkedInの投稿に貼り付けてください',
    tagline: 'すべての国がアメリカに語りかけられる。心に響く言葉に投票しよう。あなたの声も加えよう。',
    helpTitle: '使い方',
    helpIntro: 'すべての国がアメリカに語りかけ、アメリカ人も応える場所。',
    helpTap: 'カードをタップして読み、投票しよう',
    helpVote: '賛成は👍、反対は👎、最強の支持は⭐',
    helpWrite: '自分の言葉で書こう — あなたの国からの声',
    helpLanguage: 'いつでも言語を切り替えられます',
  },
  ko: {
    hey: '야, 아메리카!',
    fromTo: '{country}에서 {audience}에게:',
    america: '미국',
    world: '세계',
    writeYourOwn: '직접 쓰기',
    all: '전체',
    yourCountry: '당신의 나라',
    language: '언어',
    search: '국가 검색...',
    seenAll: '모두 봤어요!',
    votedSummary: '{n}개의 메시지에 투표했어요. 당신의 목소리도 더할래요?',
    voteCounter: '{n}표',
    emptyLang: '이 언어로 된 메시지가 아직 없어요',
    emptyLangHint: '첫 번째가 되거나 — 다른 언어를 선택하세요.',
    verifyTitle: '{n}개의 메시지에 투표했어요!',
    verifySubtitle: '투표가 반영되도록 사람임을 인증해주세요.',
    sendMagicLink: '매직 링크 보내기',
    skipForNow: '나중에',
    checkEmail: '이메일을 확인하세요!',
    sentLinkTo: '인증 링크를 보냈어요:',
    continueSwiping: '계속',
    writePlaceholder: '{audience}에게 무슨 말을 하고 싶나요?',
    startOver: '지우기',
    submit: '보내기',
    shareTitle: '이 메시지 공유',
    copyLink: '링크 복사',
  },
  vi: {
    hey: 'Này, nước Mỹ!',
    fromTo: 'Từ {country} đến {audience}:',
    america: 'nước Mỹ',
    world: 'Thế giới',
    writeYourOwn: 'Viết của riêng bạn',
    all: 'Tất cả',
    yourCountry: 'Quốc gia của bạn',
    language: 'Ngôn ngữ',
    search: 'Tìm kiếm quốc gia...',
    seenAll: 'Bạn đã xem hết rồi!',
    votedSummary: 'Bạn đã bình chọn {n} tin nhắn. Muốn thêm tiếng nói của mình?',
    voteCounter: '{n} phiếu',
    emptyLang: 'Chưa có tin nhắn nào bằng ngôn ngữ này',
    emptyLangHint: 'Hãy là người đầu tiên — hoặc chọn ngôn ngữ khác.',
    verifyTitle: 'Bạn đã bình chọn {n} tin nhắn!',
    verifySubtitle: 'Xác minh bạn là người để phiếu của bạn được tính.',
    sendMagicLink: 'Gửi liên kết',
    skipForNow: 'Để sau',
    checkEmail: 'Kiểm tra email của bạn!',
    sentLinkTo: 'Chúng tôi đã gửi liên kết xác minh đến',
    continueSwiping: 'Tiếp tục',
    writePlaceholder: 'Bạn muốn nói gì với {audience}?',
    startOver: 'Xóa',
    submit: 'Gửi',
    shareTitle: 'Chia sẻ tin nhắn này',
    copyLink: 'Sao chép liên kết',
  },
  th: {
    hey: 'เฮ้ อเมริกา!',
    fromTo: 'จาก {country} ถึง {audience}:',
    america: 'อเมริกา',
    world: 'โลก',
    writeYourOwn: 'เขียนของคุณเอง',
    all: 'ทั้งหมด',
    yourCountry: 'ประเทศของคุณ',
    language: 'ภาษา',
    search: 'ค้นหาประเทศ...',
    seenAll: 'คุณดูครบแล้ว!',
    votedSummary: 'คุณโหวต {n} ข้อความ อยากเพิ่มเสียงของคุณไหม?',
    voteCounter: '{n} โหวต',
    emptyLang: 'ยังไม่มีข้อความในภาษานี้',
    emptyLangHint: 'เป็นคนแรก — หรือเลือกภาษาอื่น',
    verifyTitle: 'คุณโหวต {n} ข้อความ!',
    verifySubtitle: 'ยืนยันว่าคุณเป็นมนุษย์เพื่อให้โหวตของคุณนับ',
    sendMagicLink: 'ส่งลิงก์',
    skipForNow: 'ข้ามไปก่อน',
    checkEmail: 'ตรวจอีเมลของคุณ!',
    sentLinkTo: 'เราส่งลิงก์ยืนยันไปยัง',
    continueSwiping: 'ดำเนินการต่อ',
    writePlaceholder: 'คุณอยากบอกอะไร{audience}?',
    startOver: 'ลบ',
    submit: 'ส่ง',
    shareTitle: 'แชร์ข้อความนี้',
    copyLink: 'คัดลอกลิงก์',
  },
  km: {
    hey: 'ហេ អាមេរិក!',
    fromTo: 'ពី {country} ទៅ {audience}៖',
    america: 'អាមេរិក',
    world: 'ពិភពលោក',
    writeYourOwn: 'សរសេរផ្ទាល់ខ្លួន',
    all: 'ទាំងអស់',
    yourCountry: 'ប្រទេសរបស់អ្នក',
    language: 'ភាសា',
    search: 'ស្វែងរកប្រទេស...',
  },
  lo: {
    hey: 'ເຮ້ ອາເມຣິກາ!',
    fromTo: 'ຈາກ {country} ເຖິງ {audience}:',
    america: 'ອາເມຣິກາ',
    world: 'ໂລກ',
    writeYourOwn: 'ຂຽນຂອງທ່ານເອງ',
    all: 'ທັງໝົດ',
    yourCountry: 'ປະເທດຂອງທ່ານ',
    language: 'ພາສາ',
    search: 'ຄົ້ນຫາປະເທດ...',
  },
  my: {
    hey: 'ဟေး အမေရိက!',
    fromTo: '{country} မှ {audience} သို့:',
    america: 'အမေရိက',
    world: 'ကမ္ဘာ',
    writeYourOwn: 'ကိုယ်ပိုင်ရေးပါ',
    all: 'အားလုံး',
    yourCountry: 'သင့်နိုင်ငံ',
    language: 'ဘာသာစကား',
    search: 'နိုင်ငံများရှာရန်...',
  },
  id: {
    hey: 'Hei Amerika!',
    fromTo: 'Dari {country} ke {audience}:',
    america: 'Amerika',
    world: 'Dunia',
    writeYourOwn: 'Tulis sendiri',
    all: 'Semua',
    yourCountry: 'Negaramu',
    language: 'Bahasa',
    search: 'Cari negara...',
    seenAll: 'Kamu sudah melihat semuanya!',
    votedSummary: 'Kamu memilih {n} pesan. Mau menambahkan suaramu?',
    voteCounter: '{n} suara',
    emptyLang: 'Belum ada pesan dalam bahasa ini',
    emptyLangHint: 'Jadilah yang pertama — atau pilih bahasa lain.',
    verifyTitle: 'Kamu memilih {n} pesan!',
    verifySubtitle: 'Verifikasi kamu manusia agar suaramu dihitung.',
    sendMagicLink: 'Kirim tautan',
    skipForNow: 'Nanti saja',
    checkEmail: 'Cek email kamu!',
    sentLinkTo: 'Kami mengirim tautan verifikasi ke',
    continueSwiping: 'Lanjutkan',
    writePlaceholder: 'Apa yang ingin kamu katakan ke {audience}?',
    startOver: 'Hapus',
    submit: 'Kirim',
    shareTitle: 'Bagikan pesan ini',
    copyLink: 'Salin tautan',
  },
  ms: {
    hey: 'Hai Amerika!',
    fromTo: 'Dari {country} ke {audience}:',
    america: 'Amerika',
    world: 'Dunia',
    writeYourOwn: 'Tulis sendiri',
    all: 'Semua',
    yourCountry: 'Negara anda',
    language: 'Bahasa',
    search: 'Cari negara...',
  },
  jv: {
    hey: 'Hai Amerika!',
    fromTo: 'Saka {country} kanggo {audience}:',
    america: 'Amerika',
    world: 'Donya',
    writeYourOwn: 'Tulis dhewe',
    all: 'Kabeh',
    yourCountry: 'Negaramu',
    language: 'Basa',
    search: 'Goleki negara...',
  },
  tl: {
    hey: 'Uy Amerika!',
    fromTo: 'Mula {country} para sa {audience}:',
    america: 'Amerika',
    world: 'Mundo',
    writeYourOwn: 'Sumulat ng sariling mensahe',
    all: 'Lahat',
    yourCountry: 'Iyong bansa',
    language: 'Wika',
    search: 'Maghanap ng bansa...',
  },
  ceb: {
    hey: 'Uy America!',
    fromTo: 'Gikan sa {country} para sa {audience}:',
    america: 'Amerika',
    world: 'Kalibutan',
    writeYourOwn: 'Sulati ang imong kaugalingon',
    all: 'Tanan',
    yourCountry: 'Imong nasud',
    language: 'Pinulongan',
    search: 'Pangita og nasud...',
  },
  sw: {
    hey: 'Habari Marekani!',
    fromTo: 'Kutoka {country} kwa {audience}:',
    america: 'Marekani',
    world: 'Dunia',
    writeYourOwn: 'Andika yako',
    all: 'Zote',
    yourCountry: 'Nchi yako',
    language: 'Lugha',
    search: 'Tafuta nchi...',
  },
  am: {
    hey: 'ሄይ አሜሪካ!',
    fromTo: 'ከ{country} ወደ {audience}:',
    america: 'አሜሪካ',
    world: 'አለም',
    writeYourOwn: 'የራስህን ጻፍ',
    all: 'ሁሉም',
    yourCountry: 'አገርህ',
    language: 'ቋንቋ',
    search: 'አገሮችን ፈልግ...',
  },
  ha: {
    hey: 'Sannu Amurka!',
    fromTo: 'Daga {country} zuwa {audience}:',
    america: 'Amurka',
    world: 'Duniya',
    writeYourOwn: 'Rubuta naka',
    all: 'Duka',
    yourCountry: 'Kasarka',
    language: 'Harshe',
    search: 'Nemo kasashe...',
  },
  ig: {
    hey: 'Ndewo America!',
    fromTo: 'Site na {country} ruo {audience}:',
    america: 'America',
    world: 'Ụwa',
    writeYourOwn: 'Dee nke gị',
    all: 'Niile',
    yourCountry: 'Obodo gị',
    language: 'Asụsụ',
    search: 'Chọọ mba...',
  },
  yo: {
    hey: 'Ẹ kú o, America!',
    fromTo: 'Lati {country} si {audience}:',
    america: 'America',
    world: 'Aye',
    writeYourOwn: 'Kọ tirẹ',
    all: 'Gbogbo',
    yourCountry: 'Orilẹ-ede rẹ',
    language: 'Ede',
    search: 'Wa awọn orilẹ-ede...',
  },
  zu: {
    hey: 'Sawubona America!',
    fromTo: 'Kusuka {country} kuya ku-{audience}:',
    america: 'America',
    world: 'Umhlaba',
    writeYourOwn: 'Bhala eyakho',
    all: 'Konke',
    yourCountry: 'Izwe lakho',
    language: 'Ulimi',
    search: 'Sesha amazwe...',
  },
  xh: {
    hey: 'Molo America!',
    fromTo: 'Ukusuka e-{country} ukuya e-{audience}:',
    america: 'America',
    world: 'Ihlabathi',
    writeYourOwn: 'Bhala eyakho',
    all: 'Konke',
    yourCountry: 'Ilizwe lakho',
    language: 'Ulwimi',
    search: 'Khangela amazwe...',
  },
  af: {
    hey: 'Haai Amerika!',
    fromTo: 'Van {country} aan {audience}:',
    america: 'Amerika',
    world: 'Wêreld',
    writeYourOwn: 'Skryf jou eie',
    all: 'Alles',
    yourCountry: 'Jou land',
    language: 'Taal',
    search: 'Soek lande...',
  },
  rw: {
    hey: 'Yewe Amerika!',
    fromTo: 'Kuva muri {country} ujya muri {audience}:',
    america: 'Amerika',
    world: 'Isi',
    writeYourOwn: 'Andika ubwawe',
    all: 'Byose',
    yourCountry: 'Igihugu cyawe',
    language: 'Ururimi',
    search: 'Shakisha ibihugu...',
  },
  so: {
    hey: 'Haye Ameerika!',
    fromTo: 'Ka {country} ilaa {audience}:',
    america: 'Ameerika',
    world: 'Adduunka',
    writeYourOwn: 'Qor kaaga',
    all: 'Dhammaan',
    yourCountry: 'Dalkaaga',
    language: 'Luqad',
    search: 'Raadi waddamada...',
  },
  mg: {
    hey: 'Salama Amerika!',
    fromTo: 'Avy any {country} ho an\'i {audience}:',
    america: 'Amerika',
    world: 'Tontolo',
    writeYourOwn: 'Soraty ny anao',
    all: 'Daholo',
    yourCountry: 'Firenenao',
    language: 'Fiteny',
    search: 'Hitady firenena...',
  },
  ga: {
    hey: 'Hé, a Mheiriceá!',
    fromTo: 'Ó {country} go {audience}:',
    america: 'Meiriceá',
    world: 'an Domhain',
    writeYourOwn: 'Scríobh do cheann féin',
    all: 'Gach rud',
    yourCountry: 'Do thír',
    language: 'Teanga',
    search: 'Cuardaigh tíortha...',
  },
  cy: {
    hey: 'Hei America!',
    fromTo: 'O {country} i {audience}:',
    america: 'America',
    world: 'y Byd',
    writeYourOwn: 'Ysgrifenna dy un dy hun',
    all: 'Pob un',
    yourCountry: 'Dy wlad',
    language: 'Iaith',
    search: 'Chwilio gwledydd...',
  },
  mt: {
    hey: 'Ej Amerika!',
    fromTo: 'Minn {country} għal {audience}:',
    america: 'Amerika',
    world: 'Dinja',
    writeYourOwn: 'Ikteb tiegħek',
    all: 'Kollha',
    yourCountry: 'Pajjiżek',
    language: 'Lingwa',
    search: 'Fittex pajjiżi...',
  },
  eu: {
    hey: 'Aupa Amerika!',
    fromTo: '{country}-tik {audience}-ra:',
    america: 'Amerika',
    world: 'Mundua',
    writeYourOwn: 'Idatzi zurea',
    all: 'Denak',
    yourCountry: 'Zure herrialdea',
    language: 'Hizkuntza',
    search: 'Bilatu herrialdeak...',
  },
  ca: {
    hey: 'Ei, Amèrica!',
    fromTo: 'De {country} a {audience}:',
    america: 'Amèrica',
    world: 'Món',
    writeYourOwn: 'Escriu el teu',
    all: 'Tot',
    yourCountry: 'El teu país',
    language: 'Idioma',
    search: 'Cerca països...',
  },
  sq: {
    hey: 'Hej Amerikë!',
    fromTo: 'Nga {country} në {audience}:',
    america: 'Amerikë',
    world: 'Botë',
    writeYourOwn: 'Shkruaje tëndin',
    all: 'Të gjitha',
    yourCountry: 'Vendi yt',
    language: 'Gjuha',
    search: 'Kërko vende...',
  },
  hy: {
    hey: 'Հե՜յ, Ամերիկա!',
    fromTo: '{country}-ից {audience}-ին:',
    america: 'Ամերիկա',
    world: 'աշխարհ',
    writeYourOwn: 'Գրիր քո սեփականը',
    all: 'Բոլորը',
    yourCountry: 'Քո երկիրը',
    language: 'Լեզու',
    search: 'Փնտրել երկրներ...',
  },
  ka: {
    hey: 'ჰეი, ამერიკა!',
    fromTo: '{country}-დან {audience}-ს:',
    america: 'ამერიკას',
    world: 'მსოფლიოს',
    writeYourOwn: 'საკუთარი დაწერე',
    all: 'ყველა',
    yourCountry: 'შენი ქვეყანა',
    language: 'ენა',
    search: 'ქვეყნების ძიება...',
  },
  kk: {
    hey: 'Эй, Америка!',
    fromTo: '{country}-дан {audience}-ке:',
    america: 'Америкаға',
    world: 'Әлемге',
    writeYourOwn: 'Өзіңдікін жаз',
    all: 'Барлығы',
    yourCountry: 'Сенің еліңе',
    language: 'Тіл',
    search: 'Елдерді іздеу...',
  },
  ky: {
    hey: 'Эй, Америка!',
    fromTo: '{country}тан {audience}ге:',
    america: 'Америкага',
    world: 'Дүйнөгө',
    writeYourOwn: 'Өзүңдүкүн жаз',
    all: 'Бардыгы',
    yourCountry: 'Сенин өлкөң',
    language: 'Тил',
    search: 'Өлкөлөрдү издөө...',
  },
  uz: {
    hey: 'Hey Amerika!',
    fromTo: '{country}dan {audience}ga:',
    america: 'Amerikaga',
    world: 'Dunyoga',
    writeYourOwn: 'O\'zingnikini yoz',
    all: 'Hammasi',
    yourCountry: 'Mamlakatingiz',
    language: 'Til',
    search: 'Mamlakatlarni qidirish...',
  },
  tk: {
    hey: 'Salam Amerika!',
    fromTo: '{country}-dan {audience}-a:',
    america: 'Amerika',
    world: 'Dünýä',
    writeYourOwn: 'Öz mesajyňyzy ýazyň',
    all: 'Ählisi',
    yourCountry: 'Ýurduňyz',
    language: 'Dil',
    search: 'Ýurtlary gözle...',
  },
  mn: {
    hey: 'Хөөе, Америк!',
    fromTo: '{country}-аас {audience}-д:',
    america: 'Америк',
    world: 'Дэлхий',
    writeYourOwn: 'Өөрийнхөө бич',
    all: 'Бүгд',
    yourCountry: 'Таны улс',
    language: 'Хэл',
    search: 'Улс хайх...',
  },
  tg: {
    hey: 'Эй, Амрико!',
    fromTo: 'Аз {country} ба {audience}:',
    america: 'Амрико',
    world: 'Ҷаҳон',
    writeYourOwn: 'Аз они худро нависед',
    all: 'Ҳама',
    yourCountry: 'Кишвари шумо',
    language: 'Забон',
    search: 'Кишварҳоро ҷустуҷӯ...',
  },
  ht: {
    hey: 'Ey Lamerik!',
    fromTo: 'Soti nan {country} pou {audience}:',
    america: 'Lamerik',
    world: 'Mond la',
    writeYourOwn: 'Ekri pa w',
    all: 'Tout',
    yourCountry: 'Peyi w',
    language: 'Lang',
    search: 'Chèche peyi...',
  },
};

const FALLBACK = T.en;

export function t(key: TKey, lang: string): string {
  return T[lang]?.[key] ?? FALLBACK[key] ?? '';
}

// Render a template like "From {country} to {audience}:" with substitutions.
export function tFromTo(lang: string, country: string, audience: string): string {
  const tmpl = t('fromTo', lang);
  return tmpl.replace('{country}', country).replace('{audience}', audience);
}

// Translation with {n} substitution, e.g. "{n} voted" → "5 voted".
export function tn(key: TKey, n: number, lang: string): string {
  return t(key, lang).replace('{n}', String(n));
}

// Translation with {audience} substitution.
export function tAudience(key: TKey, audience: string, lang: string): string {
  return t(key, lang).replace('{audience}', audience);
}

// The country name in the user's UI language, via Intl.DisplayNames.
// Covers all 195 ISO countries automatically; no static data needed.
//
// `compact: true` falls back to the locale's short style when the long name
// exceeds ~16 chars — gives "US" instead of "United States", "EE. UU." in
// Spanish, "UAE" instead of "United Arab Emirates", etc.
export function countryNameIn(code: string, lang: string, compact = false): string {
  try {
    const long = new Intl.DisplayNames([lang, 'en'], { type: 'region', style: 'long' }).of(code) || code;
    if (!compact || long.length <= 16) return long;
    const short = new Intl.DisplayNames([lang, 'en'], { type: 'region', style: 'short' }).of(code) || long;
    return short.length < long.length ? short : long;
  } catch {
    return code;
  }
}

// Back-compat: original heyAmerica helper used by page.tsx.
export function getHeyAmerica(lang: string): string {
  return t('hey', lang);
}
